import React, { PropsWithChildren, ReactElement, useRef, useState } from 'react'
import { Flex } from 'theme-ui'
import AcknowledgeDeletionCheckbox from './DialogContentElements/AcknowledgeDeletionCheckbox'
import ConfirmDeletionPassword from './DialogContentElements/ConfirmDeletionPassword'
import DeleteAndCancelButtons from './DialogContentElements/DeleteAndCancelButton'

export enum DeletionType {
  ACCOUNT = 'account',
  PROFILE = 'profile',
  ANALYTICS = 'analytics',
  ADMIN_LOGS = 'admin-logs',
  DEVICE = 'device',
  SUB_ORG = 'sub-org',
}

export interface ConfirmDeletionContentProps {
  type?: DeletionType
  checkboxText?: string
  isRequestInFlight?: boolean
  shouldShowPassword?: boolean
  onCancel?: () => void
  onDelete?: (password?: string) => void
}

const ConfirmDeletionContent = ({
  type,
  checkboxText,
  isRequestInFlight,
  shouldShowPassword,
  onDelete,
  onCancel,
  children,
}: PropsWithChildren<ConfirmDeletionContentProps>): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [acknowledgeDeletion, setAcknowledgeDeletion] = useState(false)
  const [confirmation, setConfirmation] = useState('')

  return (
    <Flex
      ref={containerRef}
      data-testid={`${type}-confirm-deletion`}
      sx={{
        position: 'relative',
        height: ['100%', 'auto'],
        width: ['100%', 'auto'],
        maxWidth: ['100%', '43.5rem'],
        pb: 0,
        flexDirection: 'column',
        justifyContent: ['flex-start', 'center'],
        alignItems: 'center',
        textAlign: 'center',
        color: 'aliceBlue60',
      }}
    >
      <Flex sx={{ px: '1.6rem', py: '3.2rem', flexDirection: 'column', alignItems: 'center' }}>
        {children}

        {shouldShowPassword && (
          <Flex sx={{ width: ['100%', '36rem'], mt: '2.4rem' }}>
            <ConfirmDeletionPassword onChange={e => setConfirmation(e.target.value)} />
          </Flex>
        )}
      </Flex>

      {checkboxText && (
        <AcknowledgeDeletionCheckbox
          isChecked={acknowledgeDeletion}
          checkboxText={checkboxText}
          onChange={checked => setAcknowledgeDeletion(checked)}
        />
      )}

      <DeleteAndCancelButtons
        onDeleteClick={() => onDelete?.(confirmation)}
        onCancelClick={onCancel}
        isDeleteButtonDisabled={
          checkboxText ? !acknowledgeDeletion : !!shouldShowPassword && !confirmation
        }
        isDeleteLoading={!!isRequestInFlight}
        deleteTestId={`delete-${type}-button`}
      />
    </Flex>
  )
}

export default ConfirmDeletionContent
